import { Box } from '@mui/material';
import { MAX_DEADLINE_DOCUMENT_SIZE_MB } from '@prospero/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeadlineDocumentField } from '../../../../components/deadline-document-reading/deadline-document-field';
import type { DeadlineDocumentReading } from '../../../../hooks/deadline/use-deadline-document-reading';
import type { DocumentUploaderGate } from './consultant-document-uploader-gate';

interface ConsultantDeadlineCreateDocumentSectionProps {
  reading: DeadlineDocumentReading;
  clientCanUpload: boolean;
  gate: DocumentUploaderGate;
  clientCount: number;
  /** Salvataggio tentato o scelta di chi carica toccata: il documento mancante si segnala anche senza aver toccato il campo. */
  isSubmitted: boolean;
}

/** Il documento: se lo carica il consulente è obbligatorio (per un solo cliente), altrimenti facoltativo. */
export function ConsultantDeadlineCreateDocumentSection({
  reading,
  clientCanUpload,
  gate,
  clientCount,
  isSubmitted,
}: ConsultantDeadlineCreateDocumentSectionProps) {
  const { t } = useTranslation();
  const { t: tDocuments } = useTranslation('documents');
  // Rosso solo dopo un'interazione con il documento: scegliere il cliente non basta.
  const [hasInteracted, setHasInteracted] = useState(false);
  const isDocumentMissing = gate === 'documentMissing';
  const showDocumentMissing = isDocumentMissing && (hasInteracted || isSubmitted);
  const trackedReading: DeadlineDocumentReading = {
    ...reading,
    pickFile: (file, options) => {
      setHasInteracted(true);
      reading.pickFile(file, options);
    },
    discard: () => {
      setHasInteracted(true);
      reading.discard();
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <DeadlineDocumentField
        reading={trackedReading}
        label={tDocuments('deadlines.aiPreview.documentLabel')}
        helper={
          clientCanUpload
            ? tDocuments('deadlines.aiPreview.documentOptionalHelper', {
                defaultValue: 'Facoltativo: puoi aggiungerlo anche dopo con «Carica il documento».',
              })
            : tDocuments('deadlines.renew.fileHelperPdfOrPhoto', {
                maxMb: MAX_DEADLINE_DOCUMENT_SIZE_MB,
              })
        }
        // "Lo carico io" dice già che il documento serve: lo ricorda l'asterisco, non un helper.
        required={!clientCanUpload}
        disabled={clientCount !== 1}
        disabledHelper={
          clientCount === 0
            ? tDocuments('deadlines.aiPreview.chooseClientFirst', {
                defaultValue: 'Scegli prima il cliente: poi alleghi il documento.',
              })
            : tDocuments('deadlines.aiPreview.singleClientOnly')
        }
        errorText={
          showDocumentMissing
            ? t('consultant.deadlines.create.documentMissing', {
                defaultValue:
                  'Scegli il documento da allegare, oppure indica che lo carica il cliente.',
              })
            : undefined
        }
        testIdPrefix="consultant-deadline-create-document"
      />
    </Box>
  );
}
